// src/pages/CartPage.jsx
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { UserContext } from '../context/UserContext';
import { useLanguage } from '../hooks/useLanguage';
import { ConfigContext } from '../context/ConfigContext';
import { NotificationContext } from '../context/NotificationContext';
import { convertCreditsToTargetCurrency, formatCurrency } from '../utils/priceCalculator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './CartPage.css';

const CartPage = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const { cart, loading, removeFromCart } = useContext(CartContext);
  const { user } = useContext(UserContext);
  const { config } = useContext(ConfigContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const totalCostInCredits = useMemo(() => {
    return cart.items.reduce((total, item) => total + item.costInCredits, 0);
  }, [cart.items]);

  const availablePoints = useMemo(() => {
    return Math.max(0, (user.creditPoints || 0) - (user.pointsOnHold || 0));
  }, [user.creditPoints, user.pointsOnHold]);

  const creditsToUse = useMemo(() => {
    return Math.min(availablePoints, totalCostInCredits);
  }, [availablePoints, totalCostInCredits]);

  const { totalCostInCurrency, creditsValueInCurrency, finalAmountInCurrency, rates, currency } = useMemo(() => {
    if (!config || !user.isLoggedIn) {
      return {
        totalCostInCurrency: '...',
        creditsValueInCurrency: '...',
        finalAmountInCurrency: '...',
        rates: null,
        currency: null,
      };
    }
    const localRates = {
      usdToIlsRate: config.usdToIlsRate,
      eurToIlsRate: config.eurToIlsRate,
      creditPointValueILS: config.creditPointValueILS,
    };
    const localCurrency = user.preferences.currency;

    const totalCost = formatCurrency(convertCreditsToTargetCurrency(totalCostInCredits, localCurrency, localRates), localCurrency);
    const creditsValue = formatCurrency(convertCreditsToTargetCurrency(creditsToUse, localCurrency, localRates), localCurrency);
    const finalAmount = formatCurrency(convertCreditsToTargetCurrency(totalCostInCredits - creditsToUse, localCurrency, localRates), localCurrency);

    return {
      totalCostInCurrency: totalCost,
      creditsValueInCurrency: creditsValue,
      finalAmountInCurrency: finalAmount,
      rates: localRates,
      currency: localCurrency,
    };
  }, [totalCostInCredits, creditsToUse, user, config]);

  const handleRemoveItem = async (itemId, itemName) => {
    addNotification(
      t('cartPage.removeItem.confirmation', { itemName }),
      'warning',
      0,
      true, // isConfirmation
      [
        { text: t('cartPage.removeItem.cancel'), onClick: () => {} },
        {
          text: t('cartPage.removeItem.confirm'),
          onClick: async () => {
            const result = await removeFromCart(itemId);
            if (result.success) {
              addNotification(t('cartPage.removeItem.success', { itemName }), 'success');
            }
          },
          className: 'primary',
        },
      ]
    );
  };

  const handleCheckout = () => {
    // For now, this just navigates. The actual checkout logic will be on the checkout page.
    navigate(`/${lang}/checkout`);
  };

  if (loading) {
    return (
      <div className="cart-page-container loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>{t('cartPage.loading')}</p>
      </div>
    );
  }

  return (
    <div className="cart-page-container">
      <h1>{t('cartPage.title')}</h1>
      {cart.items.length === 0 ? (
        <div className="empty-cart-message">
          <p>{t('cartPage.empty.message')}</p>
          <Link to={`/${lang}/map`} className="btn btn-primary">
            {t('cartPage.empty.continueShopping')}
          </Link>
        </div>
      ) : (
        <div className="cart-content">
          <div className="cart-items-list">
            {cart.items.map((item) => {
              const itemCostInCurrency = rates && currency ? formatCurrency(convertCreditsToTargetCurrency(item.costInCredits, currency, rates), currency) : '...';

              return (
                <div key={item._id} className="cart-item">
                  <div className="item-details">
                    <span className="item-name">{item.name}</span>
                    <span className="item-cost">{itemCostInCurrency}</span>
                  </div>
                  <button className="remove-item-btn" onClick={() => handleRemoveItem(item._id, item.name)} title={t('cartPage.item.removeTitle')}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="cart-summary">
            <h2>{t('cartPage.summary.title')}</h2>
            <div className="summary-line">
              <span>{t('cartPage.summary.totalCost')}</span>
              <span>{totalCostInCurrency}</span>
            </div>
            <div className="summary-line">
              <span>{t('cartPage.summary.creditsUsed', { count: creditsToUse })}</span>
              <span>- {creditsValueInCurrency}</span>
            </div>
            <hr />
            <div className="summary-line total">
              <span>{t('cartPage.summary.amountToPay')}</span>
              <span>~ {finalAmountInCurrency}</span>
            </div>
            <p className="disclaimer">{t('cartPage.summary.disclaimer')}</p>
            <button className="btn btn-primary checkout-btn" onClick={handleCheckout}>
              {t('cartPage.summary.checkoutButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
